/**
 * ч.951 (баг власника): фільтри сторінки ГУБИЛИСЬ при переході на іншу
 * сторінку і назад — вони жили у звичайному `useState`, а React розмонтовує
 * компонент при зміні маршруту.
 *
 * `useStickyState` — заміна `useState` один-в-один (та сама пара
 * `[value, setValue]`, підтримує функціональний сеттер), але значення
 * зберігається у `uiPrefs` (родина `filters:`) — тобто у ПРОФІЛІ юзера:
 * фільтри переживають і перехід між сторінками, і F5, і інший компʼютер.
 *
 *   const [orgId, setOrgId] = useStickyState('reconciliation-act.orgId', '');
 *
 * ч.1229 (баг власника: «змінюю клієнта — поле організації порожнє і помилка»): ключ
 * містить КЛІЄНТА. Профіль користувача — один на всі його клієнти, а у фільтрах лежать
 * ідентифікатори організації, контрагента, договору ОДНОГО клієнта; без клієнта в ключі
 * «Акт звірки» і «Розрахунки за документами» в іншому клієнті відкривались з чужою
 * організацією. Старі ключі без клієнта прибирає `dropLegacyStickyFilters`.
 */
import { useCallback, useState } from 'react';
import { getActiveTenantId } from '@/auth/store';
import { uiPrefs } from '@/store/uiPrefs';

const PREFIX = 'filters:';
const TENANT_SCOPED = /^filters:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:/i;

/** Ключ фільтра в профілі: `filters:<клієнт>:<сторінка.поле>`. */
export function stickyKey(key: string, tenantId: string | null = getActiveTenantId()): string {
  return `${PREFIX}${tenantId ?? 'no-tenant'}:${key}`;
}

/** Прибрати з профілю фільтри, збережені до ч.1229 без клієнта (чиї вони — невідомо). */
export function dropLegacyStickyFilters(): void {
  for (const k of uiPrefs.keys()) {
    if (k.startsWith(PREFIX) && !TENANT_SCOPED.test(k)) uiPrefs.remove(k);
  }
}

export function useStickyState<T>(
  key: string, initial: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const fullKey = stickyKey(key);
  const [state, setState] = useState<T>(() => {
    const saved = uiPrefs.get<T>(fullKey);
    return saved === null || saved === undefined ? initial : saved;
  });
  const set = useCallback((v: T | ((prev: T) => T)) => {
    setState((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      uiPrefs.set(fullKey, next);
      return next;
    });
  }, [fullKey]);
  return [state, set];
}

export default useStickyState;
